import {
  AppBar,
  Avatar,
  ButtonBase,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import React, { forwardRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import HelpIcon from '@mui/icons-material/Help';
import LogoutIcon from '@mui/icons-material/Logout';
import TerminalIcon from '@mui/icons-material/Terminal';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Logo from '../../ui/icons/Logo';
import { NAV_COLLAPSED_WIDTH } from '../../../shared/constants';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../features/auth/hooks/useAuth';
import { PermissionGate } from '../../../features/permissions/components/PermissionGate';
import './header.css';

interface HeaderProps {
  onMenuToggle: () => void;
}

function Header(
  { onMenuToggle }: HeaderProps,
  menuButtonRef: React.ForwardedRef<HTMLButtonElement>,
) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation('common');
  const { logout, username, role } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const backToHomepage = () => {
    navigate('/');
  };

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);
  const handleLogout = () => {
    handleCloseMenu();
    logout();
  };
  const handleOpenTechnicalLogs = () => {
    handleCloseMenu();
    navigate('/event-inbox');
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: theme => theme.zIndex.drawer + 1,
        height: theme.toolbar.height,
        bgcolor: theme.palette.primary.main,
        overflow: 'visible',
      }}
    >
      <Toolbar
        variant="dense"
        sx={{ minHeight: `${theme.toolbar.height}px !important`, px: 0, alignItems: 'center' }}
      >
        <div className="header-menu-button">
          <IconButton
            ref={menuButtonRef}
            color="inherit"
            aria-label={t('headerAria.openDrawer')}
            onClick={onMenuToggle}
            sx={{ p: 0 }}
          >
            <MenuIcon />
          </IconButton>
        </div>

        {!isMobile && (
          <ButtonBase
            onClick={backToHomepage}
            aria-label={t('headerAria.backToHomepage')}
            className="header-logo-button"
            sx={{ position: 'absolute', left: NAV_COLLAPSED_WIDTH, zIndex: 1, borderRadius: '50%' }}
          >
            <Avatar
              sx={{
                bgcolor: theme.palette.common.white,
                height: theme.avatar.lg,
                width: theme.avatar.lg,
              }}
            >
              <Logo size={30} />
            </Avatar>
          </ButtonBase>
        )}

        <div className="header-spacer" />

        <Box
          component="button"
          onClick={handleOpenMenu}
          aria-label={t('icons.profile')}
          aria-haspopup="true"
          aria-expanded={Boolean(anchorEl)}
          aria-controls="profile-menu"
          className="header-profile-button"
        >
          <AccountCircleIcon className="header-profile-icon" />
          {username && (
            <Typography variant="body2" className="header-username">
              {username}
            </Typography>
          )}
        </Box>

        <Menu
          id="profile-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                minWidth: 180,
                color: theme.palette.text.label,
                '& .MuiListItemIcon-root': { minWidth: '30px' },
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {/* Non-interactive : role="presentation" retire l'élément de la navigation clavier
              tout en restant visible — préférable à "disabled" qui masque aux screen readers */}
          <MenuItem
            role="presentation"
            tabIndex={-1}
            className={`header-menu-item header-menu-item--static`}
          >
            <ListItemIcon sx={{ color: theme.palette.text.label }}>
              <AccountCircleIcon fontSize="small" />
            </ListItemIcon>
            {username}
          </MenuItem>

          <Divider />

          {role === 'admin' && [
            <MenuItem
              key="technical-logs"
              aria-label={t('icons.technicalLogs')}
              onClick={handleOpenTechnicalLogs}
              className="header-menu-item"
            >
              <ListItemIcon sx={{ color: theme.palette.text.label }}>
                <TerminalIcon fontSize="small" />
              </ListItemIcon>
              {t('icons.technicalLogs')}
            </MenuItem>,
            <Divider key="technical-logs-divider" />,
          ]}

          {/* « Aide » : masquée sans display sur user_menu.help (HOMCO-640). Le nom
              et la Déconnexion restent toujours visibles. */}
          <PermissionGate line="user_menu.help">
            <MenuItem aria-label={t('icons.help')} className="header-menu-item">
              <ListItemIcon sx={{ color: theme.palette.text.label }}>
                <HelpIcon fontSize="small" />
              </ListItemIcon>
              {t('icons.help')}
            </MenuItem>

            <Divider />
          </PermissionGate>

          <MenuItem
            aria-label={t('icons.logout')}
            onClick={handleLogout}
            className="header-menu-item"
          >
            <ListItemIcon sx={{ color: theme.palette.text.label }}>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            {t('icons.logout')}
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}

export default forwardRef<HTMLButtonElement, HeaderProps>(Header);
